/**
 * Living SOPs Analytics Hook (Phase 23.5.3)
 *
 * TanStack Query hook for the manager-gated Living SOPs analytics dashboard.
 * Wraps GET /api/analytics/living-sops.
 *
 * Response powers the KPI tiles from blueprint §6:
 *   - Proposal volumes (by source + by state)
 *   - Approval rates (overall + per source)
 *   - Delta signal-to-noise ratio — the hero KPI
 *   - Cascade latency (p50 / p90 ms, create -> publish)
 *   - Delta outcome tags — post-hoc improved/unchanged/regressed labels on
 *     approved performance-delta proposals with 30d of post-publish data.
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// ============================================
// QUERY KEYS
// ============================================

export const livingSopsAnalyticsKeys = {
  all: ['living-sops-analytics'] as const,
  dashboard: (dateRange: LivingSopsDateRange) =>
    [...livingSopsAnalyticsKeys.all, 'dashboard', dateRange] as const,
};

// ============================================
// TYPES
// ============================================

export type LivingSopsDateRange = '7d' | '30d' | '90d';

export type LivingSopsProposalSource =
  | 'struggle_signal'
  | 'performance_delta'
  | 'coach_mining'
  | 'manual';

// All 7 values from `sopDraftProposalStateEnum`. `draft` and `expired` are
// currently unused in V1 but declared in the schema; we keep them here so the
// dashboard keeps adding up to 100% if a future chunk starts writing them.
export type LivingSopsProposalState =
  | 'draft'
  | 'pending_review'
  | 'approved'
  | 'published'
  | 'rejected'
  | 'superseded'
  | 'expired';

export interface LivingSopsApprovalRate {
  approved: number;
  rejected: number;
  /** Percentage 0-100, one-decimal precision. 0 when no decisions exist. */
  rate: number;
}

export type LivingSopsOutcomeTag =
  | 'improved'
  | 'unchanged'
  | 'regressed'
  | 'insufficient_data';

export interface LivingSopsOutcomeTagRow {
  proposalId: string;
  sopId: string;
  sectionTitle: string | null;
  /** ISO string. */
  publishedAt: string;
  /** Pre-publish 30d average composite score. Null when <5 assessments. */
  beforeScore: number | null;
  /** Post-publish 30d average composite score. Null when <5 assessments. */
  afterScore: number | null;
  tag: LivingSopsOutcomeTag;
  /** afterScore - beforeScore, rounded to 2 decimals. Null when either side is null. */
  delta: number | null;
}

export interface LivingSopsAnalyticsResponse {
  dateRange: {
    start: string;
    end: string;
  };
  proposalVolumes: {
    bySource: Record<LivingSopsProposalSource, number>;
    byState: Record<LivingSopsProposalState, number>;
    total: number;
  };
  approvalRates: {
    overall: LivingSopsApprovalRate;
    bySource: Record<LivingSopsProposalSource, LivingSopsApprovalRate>;
  };
  deltaSignalToNoiseRatio: {
    approved: number;
    rejected: number;
    /** Percentage 0-100, one-decimal precision. 0 when no decisions. */
    snr: number;
  };
  cascadeLatency: {
    p50Ms: number;
    p90Ms: number;
    sampleSize: number;
  };
  deltaOutcomeTags: LivingSopsOutcomeTagRow[];
}

// ============================================
// HOOK
// ============================================

export function useLivingSopsAnalytics(
  dateRange: LivingSopsDateRange = '30d'
) {
  const api = useApi();

  return useQuery({
    queryKey: livingSopsAnalyticsKeys.dashboard(dateRange),
    queryFn: () =>
      api.get<LivingSopsAnalyticsResponse>(
        `/api/analytics/living-sops?dateRange=${dateRange}`
      ),
    // 60s staleTime — dashboard data is not real-time but the blueprint wants
    // on-demand recomputation to feel fresh during staging/QA.
    staleTime: 60 * 1000,
  });
}
